import { get, post, patch, del } from './https';
import { GetPresignedUrl, UploadImageToS3 } from './service';

/* ──────────────────────────────────────────────────────────────────────────
 * Admin client for the fresh Service-Booking catalog (backend: /api/v1/sb/*).
 * Catalog nodes (job types / appliances / components) carry images managed
 * with the same presigned-URL + S3 flow the Service module already uses.
 * ────────────────────────────────────────────────────────────────────────── */

const BASE = '/api/v1/sb';

// S3 folder types reused from the Service module (see backend getFolderPath):
//   8 -> service/icons   |   1 -> service/banner
export const SB_ICON_FOLDER = 8;
export const SB_BANNER_FOLDER = 1;

export interface SbJobType {
  _id?: string;
  key: string;
  label: string;
  group: 'primary' | 'secondary';
  icon?: string;
  images?: string[];
  sortOrder?: number;
  isActive?: boolean;
}

export interface SbAppliance {
  _id?: string;
  key: string;
  label: string;
  description?: string;
  supportedJobTypeKeys?: string[];
  icon?: string;
  images?: string[];
  sortOrder?: number;
  isActive?: boolean;
}

export interface SbComponent {
  _id?: string;
  key: string;
  label: string;
  applianceKeys: string[];
  triggersIntakeForm?: boolean;
  basePrice?: number;
  priceByAppliance?: Record<string, number>;
  icon?: string;
  images?: string[];
  sortOrder?: number;
  isActive?: boolean;
}

export interface SbAttributeOption {
  _id?: string;
  listKey: string;
  value: string;
  label: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface SbCatalog {
  jobTypes: SbJobType[];
  appliances: SbAppliance[];
  components: SbComponent[];
  attributeOptions: SbAttributeOption[];
}

const unwrap = (body: any) => body?.data ?? body;

// ── Public catalog reads (used by booking-create to drive equipment counters) ─
export const getBookingComponents = async (
  appliance?: string,
): Promise<SbComponent[]> => {
  const url = appliance ? `${BASE}/components?appliance=${appliance}` : `${BASE}/components`;
  const res = await get(url);
  return unwrap(res.data) || [];
};

// Primary = booking-based enquiries (Service / Repair / Installation …)
export const getBookingJobTypes = async (
  group?: 'primary' | 'secondary',
): Promise<SbJobType[]> => {
  const url = group ? `${BASE}/job-types?group=${group}` : `${BASE}/job-types`;
  const res = await get(url);
  return unwrap(res.data) || [];
};

// Appliances (AC / HP A2A / HP A2W / Boiler), optionally for one enquiry type
export const getBookingAppliances = async (
  jobType?: string,
): Promise<SbAppliance[]> => {
  const url = jobType ? `${BASE}/appliances?jobType=${jobType}` : `${BASE}/appliances`;
  const res = await get(url);
  return unwrap(res.data) || [];
};

// Intake-form dropdowns for the "Other" branch (ac_type | problem_list)
export const getBookingAttributeOptions = async (
  list: string,
): Promise<SbAttributeOption[]> => {
  const res = await get(`${BASE}/attribute-options?list=${list}`);
  return unwrap(res.data) || [];
};

// ── Read the whole catalog (admin — includes inactive) ──────────────────────
export const getCatalog = async (): Promise<SbCatalog> => {
  const res = await get(`${BASE}/admin/catalog`);
  return unwrap(res.data);
};

// ── Seed the whiteboard catalog (idempotent) ────────────────────────────────
export const seedCatalog = async (): Promise<any> => {
  const res = await post(`${BASE}/admin/seed`);
  return unwrap(res.data);
};

// ── Upserts (create or update by key) ───────────────────────────────────────
export const upsertJobType = async (body: SbJobType) => {
  const res = await post(`${BASE}/admin/job-types`, body);
  return unwrap(res.data);
};
export const upsertAppliance = async (body: SbAppliance) => {
  const res = await post(`${BASE}/admin/appliances`, body);
  return unwrap(res.data);
};
export const upsertComponent = async (body: SbComponent) => {
  const res = await post(`${BASE}/admin/components`, body);
  return unwrap(res.data);
};
export const upsertAttributeOption = async (body: SbAttributeOption) => {
  const res = await post(`${BASE}/admin/attribute-options`, body);
  return unwrap(res.data);
};

// ── Deletes (also clean up S3 media on the backend) ─────────────────────────
export const deleteJobType = (keyOrId: string) =>
  del(`${BASE}/admin/job-types/${keyOrId}`);
export const deleteAppliance = (keyOrId: string) =>
  del(`${BASE}/admin/appliances/${keyOrId}`);
export const deleteComponent = (keyOrId: string) =>
  del(`${BASE}/admin/components/${keyOrId}`);

// ── Image upload helper (presigned PUT -> public URL), reusing Service flow ──
export const uploadCatalogImage = async (
  file: File,
  folder: number = SB_ICON_FOLDER,
): Promise<string> => {
  const presignedUrl = await GetPresignedUrl(file.name, file.type, folder);
  return UploadImageToS3(file, presignedUrl);
};
